import { Button } from '@/components/ui/button'
import { Drawer, DrawerContent, DrawerHeader, DrawerTrigger } from '@/components/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { getKindFilterOptions } from '@/lib/kind-filter'
import { TKindGroup } from '@/types/window'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { useAtomValue } from 'jotai'
import { HugeiconsIcon } from '@hugeicons/react'
import { FilterHorizontalIcon } from '@hugeicons/core-free-icons'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'

export default function KindFilter({
  showKinds,
  onShowKindsChange
}: {
  showKinds: number[]
  onShowKindsChange: (kinds: number[]) => void
}) {
  const { t } = useTranslation()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [open, setOpen] = useState(false)
  const [temporaryShowKinds, setTemporaryShowKinds] = useState(showKinds)

  const { value: options = [] } = useAsync(async () => await getKindFilterOptions(), [])
  const allKinds = useMemo(() => options.flatMap((o) => o.kinds), [options])
  const defaultShowKinds = useMemo(
    () => options.filter((o) => !o.disabled).flatMap((o) => o.kinds),
    [options]
  )

  const isDifferentFromDefault = useMemo(
    () => !isSameKindFilter(showKinds, defaultShowKinds),
    [showKinds, defaultShowKinds]
  )
  const isTemporaryDifferentFromDefault = useMemo(
    () => !isSameKindFilter(temporaryShowKinds, defaultShowKinds),
    [temporaryShowKinds, defaultShowKinds]
  )

  useEffect(() => {
    setTemporaryShowKinds(showKinds)
  }, [open])

  const handleApply = () => {
    if (temporaryShowKinds.length === 0) {
      // must select at least one kind
      return
    }

    const newShowKinds = [...temporaryShowKinds].sort()
    if (!isSameKindFilter(newShowKinds, showKinds)) {
      onShowKindsChange(newShowKinds)
    }

    setOpen(false)
  }

  const trigger = (
    <Button
      variant="ghost"
      size="titlebar-icon"
      className={cn(
        'relative w-fit px-3 focus:text-foreground',
        !isDifferentFromDefault && 'text-muted-foreground'
      )}
      onClick={() => {
        if (isSmallScreen) {
          setOpen(true)
        }
      }}
    >
      <HugeiconsIcon icon={FilterHorizontalIcon} size={16} />
      <span className="hidden lg:block">{t('Kinds')}</span>
      {isDifferentFromDefault && (
        <div className="absolute size-2 rounded-full bg-primary left-7 top-2 ring-2 ring-background" />
      )}
    </Button>
  )

  const content = (
    <div>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => (
          <KindFilterOptionRow
            key={option.label}
            option={option}
            temporaryShowKinds={temporaryShowKinds}
            setTemporaryShowKinds={setTemporaryShowKinds}
          />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <Button
          variant="secondary"
          onClick={() => {
            setTemporaryShowKinds(allKinds)
          }}
        >
          {t('Select All')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setTemporaryShowKinds([])
          }}
        >
          {t('Clear All')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => setTemporaryShowKinds(defaultShowKinds)}
          disabled={!isTemporaryDifferentFromDefault}
        >
          {t('Reset')}
        </Button>
      </div>

      <Button
        onClick={handleApply}
        className="mt-4 w-full"
        disabled={temporaryShowKinds.length === 0}
      >
        {t('Apply')}
      </Button>
    </div>
  )

  if (isSmallScreen) {
    return (
      <>
        {trigger}
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild></DrawerTrigger>
          <DrawerContent className="px-4">
            <DrawerHeader />
            {content}
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-96" collisionPadding={16} sideOffset={0}>
        {content}
      </PopoverContent>
    </Popover>
  )
}

function KindFilterOptionRow({
  option,
  temporaryShowKinds,
  setTemporaryShowKinds
}: {
  option: TKindGroup
  temporaryShowKinds: number[]
  setTemporaryShowKinds: React.Dispatch<React.SetStateAction<number[]>>
}) {
  const { t } = useTranslation()
  const { kinds: kindGroup, label } = option
  const checked = kindGroup.every((k) => temporaryShowKinds.includes(k))
  return (
    <div
      className={cn(
        'cursor-pointer grid gap-1.5 rounded-lg border px-4 py-3',
        checked
          ? 'border-primary/60 bg-primary/5'
          : 'cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30'
      )}
      onClick={() => {
        if (!checked) {
          setTemporaryShowKinds((prev) => Array.from(new Set([...prev, ...kindGroup])))
        } else {
          setTemporaryShowKinds((prev) => prev.filter((k) => !kindGroup.includes(k)))
        }
      }}
    >
      <p className="leading-none font-medium">{t(label!)}</p>
      <p className="text-muted-foreground text-xs">kind {kindGroup.join(', ')}</p>
    </div>
  )
}

function isSameKindFilter(a: number[], b: number[]) {
  if (a.length !== b.length) {
    return false
  }
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((value, index) => value === sortedB[index])
}
